import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToolbar,
    useIonRouter,
    useIonToast,
    useIonViewWillEnter
} from "@ionic/react";
import {
    arrowForwardOutline,
    eyeOffOutline,
    eyeOutline,
    americanFootballOutline,
    lockClosedOutline,
    personOutline,
    shieldCheckmarkOutline
} from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import {
    useHistory
} from "react-router-dom";
import {
    AUTH_CHANGED_EVENT,
    isAuthenticated,
    setAuthentication
} from "../services/auth";
import {
    fantasyPushSync
} from "../services/fantasyPushNotifications";

import "./LogIn.css";

interface UserData {
    id: string;
    nombre: string;
    password: string;
}

type UsersByUsername = Record<string, UserData>;

type UsersFile =
    | UsersByUsername
    | UsersByUsername[];

    function findUser(
    data: UsersFile,
    username: string
): {
    username: string;
    user: UserData;
} | null {
    const normalizedInput = username
        .trim()
        .toLowerCase();

    /*
        Soporta ambos formatos:

        {
            "Einar": { ... }
        }

        [
            {
                "Einar": { ... }
            }
        ]
    */
    const userGroups = Array.isArray(data)
        ? data
        : [data];

    for (const group of userGroups) {
        for (const [storedUsername, user] of Object.entries(group)) {
            if (
                storedUsername
                    .trim()
                    .toLowerCase() === normalizedInput
            ) {
                return {
                    username: storedUsername,
                    user
                };
            }
        }
    }

    return null;
}


const LogIn: React.FC = () => {
    const router = useIonRouter();
    const [present] = useIonToast();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formVersion, setFormVersion] = useState(0);

    const history = useHistory();

    const [authenticated, setAuthenticated] = useState(
    function () {
        return isAuthenticated();
    }
);

    const contentRef = useRef<HTMLIonContentElement>(null);

const passwordInputRef = useRef<HTMLIonInputElement>(null);

function movePasswordAboveKeyboard(): void {
    /*
        Se espera a que el navegador o dispositivo abra el teclado
        y ajuste el viewport antes de calcular la posición.
    */
    window.setTimeout(async function () {
        const content = contentRef.current;
        const passwordInput = passwordInputRef.current;

        if (!content || !passwordInput) {
            return;
        }

        const scrollElement = await content.getScrollElement();

        const inputRectangle =
            passwordInput.getBoundingClientRect();

        /*
            Mantiene el campo aproximadamente en el primer tercio
            de la zona visible, por encima del teclado virtual.
        */
        const visualHeight =
            window.visualViewport?.height ||
            window.innerHeight;

        const preferredTop = Math.min(
            145,
            Math.round(visualHeight * 0.3)
        );

        const targetScrollTop = Math.max(
            0,
            scrollElement.scrollTop +
                inputRectangle.top -
                preferredTop
        );

        await content.scrollToPoint(
            0,
            targetScrollTop,
            280
        );
    }, 260);
}
    function presentToast(
        message: string,
        color: "success" | "danger"
    ) {
        present({
            message,
            duration: 2200,
            position: "bottom",
            color,
            buttons: [
                {
                    text: "Cerrar",
                    role: "cancel"
                }
            ]
        });
    }

    async function sendDataToPHP() {
        const normalizedUsername = username.trim();

        if (!normalizedUsername || !password) {
            presentToast(
                "Ingresa tu usuario y contraseña.",
                "danger"
            );

            return;
        }

        try {
            setLoading(true);

            const dataRoot = process.env.PUBLIC_URL || "";

            const response = await fetch(
                `${dataRoot}/data/usuarios.json?t=${Date.now()}`,
                {
                    cache: "no-store"
                }
            );

            if (!response.ok) {
                throw new Error(
                    `No fue posible cargar usuarios. Código: ${response.status}`
                );
            }

            const usersData = (await response.json()) as UsersFile;

const userResult = findUser(
    usersData,
    normalizedUsername
);

if (
    !userResult ||
    userResult.user.password !== password
) {
    presentToast(
        "El usuario o la contraseña no son correctos.",
        "danger"
    );

    return;
}

const user = userResult.user;

/*
    Se conserva el username original del JSON.
    Por ejemplo: "Einar", no necesariamente "einar".
*/
const storedUsername = userResult.username;


/*
    La sesión se conserva durante 30 días.
    El usuario puede cerrarla manualmente desde su cuenta.
*/
const sessionDurationInMilliseconds =
    30 * 24 * 60 * 60 * 1000;

const expiresAt = new Date(
    Date.now() + sessionDurationInMilliseconds
);

setAuthentication(
    String(user.id),
    String(user.nombre),
    storedUsername,
    expiresAt
);

setAuthenticated(true);

/*
    Si este dispositivo ya tenía permiso de notificaciones,
    actualiza silenciosamente su asociación con el usuario
    que acaba de iniciar sesión.
*/
void fantasyPushSync({
    accessToken: String(user.id),
    accessUsername: storedUsername
}).catch(function (error: unknown) {
    console.warn(
        "No fue posible sincronizar las notificaciones:",
        error
    );
});

presentToast(
    `Bienvenido, ${user.nombre}.`,
    "success"
);

history.push("/home");


        } catch (error: unknown) {

            console.error("No fue posible iniciar sesión:", error);

            presentToast(
                "No fue posible iniciar sesión. Intenta nuevamente.",
                "danger"
            );
        } finally {
            setLoading(false);
        }
    }

useIonViewWillEnter(
    function () {
        const hasActiveSession = isAuthenticated();

        setAuthenticated(hasActiveSession);

        /*
            Si Login vuelve a mostrarse sin sesión, limpiamos
            el formulario que Ionic pudo haber mantenido en caché.
        */
        if (!hasActiveSession) {
            setLoading(false);
            setUsername("");
            setPassword("");
            setShowPassword(false);

            setFormVersion(function (currentVersion) {
                return currentVersion + 1;
            });
        }
    }
);

useEffect(
    function () {
        function synchronizeAuthentication(): void {
            setAuthenticated(
                isAuthenticated()
            );
        }

        window.addEventListener(
            AUTH_CHANGED_EVENT,
            synchronizeAuthentication
        );

        return function () {
            window.removeEventListener(
                AUTH_CHANGED_EVENT,
                synchronizeAuthentication
            );
        };
    },
    []
);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="login-toolbar">
                    <IonTitle>Acceso</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent
    ref={contentRef}
    fullscreen
    className="login-content"
>

                <section className="login-hero">
                    <div className="login-hero__icon">
                        <IonIcon icon={americanFootballOutline} />
                    </div>

                    <span>MEXFBA</span>

                    <h1>Bienvenido de vuelta</h1>

                    <p>
                        Inicia sesión para acceder a Fantasy y administrar
                        tus ligas.
                    </p>
                </section>

                <main className="login-main">
                    <section
    key={formVersion}
    className="login-card"
>

                        <div className="login-card__heading">
                            <span>CUENTA MEXFBA</span>
                            <h2>Iniciar sesión</h2>
                            <p>
                                Ingresa tus datos para continuar.
                            </p>
                        </div>

                        <IonItem
                            className="login-input"
                            lines="none"
                        >
                            <IonIcon
                                slot="start"
                                icon={personOutline}
                            />

                            <IonLabel position="stacked">
                                Usuario
                            </IonLabel>

                            <IonInput
                                value={username}
                                autocomplete="username"
                                autocapitalize="off"
                                disabled={loading}
                                inputmode="text"
                                placeholder="Tu nombre de usuario"
                                onIonChange={function (event) {
                                    setUsername(
                                        event.detail.value || ""
                                    );
                                }}
                            />
                        </IonItem>

                        <IonItem
                            className="login-input"
                            lines="none"
                        >
                            <IonIcon
                                slot="start"
                                icon={lockClosedOutline}
                            />

                            <IonLabel position="stacked">
                                Contraseña
                            </IonLabel>

                            <IonInput
    ref={passwordInputRef}
    value={password}
    autocomplete="current-password"
    disabled={loading}
    placeholder="Tu contraseña"
    type={
        showPassword
            ? "text"
            : "password"
    }
    onIonFocus={movePasswordAboveKeyboard}
    onIonChange={function (event) {
        setPassword(
            event.detail.value || ""
        );
    }}
/>


                            <IonButton
    slot="end"
    fill="clear"
    color="medium"
    disabled={loading}
    aria-label={
        showPassword
            ? "Ocultar contraseña"
            : "Mostrar contraseña"
    }
    onMouseDown={function (event) {
        /*
            Mantiene el foco en el campo de contraseña
            y evita cerrar el teclado al tocar el icono.
        */
        event.preventDefault();
    }}
    onClick={function () {
        setShowPassword(!showPassword);
    }}
>
    <IonIcon
        icon={
            showPassword
                ? eyeOffOutline
                : eyeOutline
        }
    />
</IonButton>

                        </IonItem>

                        <IonButton
                            className="login-submit"
                            expand="block"
                            disabled={loading}
                            onClick={sendDataToPHP}
                        >
                            {loading ? (
                                <>
                                    <IonSpinner name="crescent" />
                                    Verificando acceso...
                                </>
                            ) : (
                                <>
                                    Iniciar sesión
                                    <IonIcon
                                        slot="end"
                                        icon={arrowForwardOutline}
                                    />
                                </>
                            )}
                        </IonButton>

                        <div className="login-security">
                            <IonIcon icon={shieldCheckmarkOutline} />

                            <span>
                                Tu sesión se mantiene activa en este dispositivo.
                            </span>
                        </div>
                    </section>
                </main>
            </IonContent>
        </IonPage>
    );
};

export default LogIn;
